import React, {useContext, useEffect, useRef, useState} from 'react';
import './style.scss';
import ProposedRouteContainer from "../../Redux/containers/ProposedRouteContainer";
import {AppContext} from "../Utils/AppContext";
import {diffArrays} from "../../Utils/DiffUtils";
import {DragDropContext} from "react-beautiful-dnd";
import DragToNewRoute from "./ProposedRoute/DragToNewRoute/DragToNewRoute";

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export default function ProposedRoutes(props) {
    const appContext = useContext(AppContext);
    const [routes, setRoutes] = useState([]);
    const timeoutRef = useRef(null);
    const [isDraggingOutside, setIsDraggingOutside] = useState(false);
    const [drag, setDrag] = useState(null);

    function routesComparator(a, b) {
        if (a.time_till_start > b.time_till_start)
            return 1;
        else if (a.time_till_start < b.time_till_start)
            return -1;

        const aRouteColorIndex = appContext.iconColors.getColorByGroupId(a.id).colorIndex;
        const bRouteColorIndex = appContext.iconColors.getColorByGroupId(b.id).colorIndex;

        if (aRouteColorIndex > bRouteColorIndex)
            return 1;
        else if (aRouteColorIndex < bRouteColorIndex)
            return -1;

        return 0;
    }

    function getRoutesChanges(oldRoutes, newRoutes) {
        const changesSet = new Set();

        const diffs = diffArrays(oldRoutes, newRoutes, (obj) => obj.id);

        for (let diff of diffs) {
            if (diff.type === "add")
                changesSet.add("ADD");
            else if (diff.type === "remove")
                changesSet.add("REMOVE");
            else if (diff.type === "update") {
                for (let change of diff.changes) {
                    changesSet.add(`UPDATE-${change.path[0]}`);
                }
            }
        }

        return [...changesSet];
    }

    function updateRoutes(newRoutes, isAnimated) {
        if (isAnimated) {
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(() => {
                newRoutes.sort(routesComparator);
                setRoutes(newRoutes);
                timeoutRef.current = undefined;
            }, 500);
        } else {
            newRoutes.sort(routesComparator);
            setRoutes(newRoutes);
        }
    }

    function isOneOfRegexes(regexes, value) {
        const re = new RegExp(regexes.join("|"), "i");
        return re.test(value);
    }

    useEffect(() => {
        const newRoutes = props.routes ? [...props.routes] : [];

        const routesChanges = getRoutesChanges(routes, newRoutes);

        const animatedChanges = ["ADD", "REMOVE", "UPDATE-route"];
        const nonAnimatedChanges = ["UPDATE-.+"];

        if (routesChanges.some(change => isOneOfRegexes(animatedChanges, change)))
            updateRoutes(newRoutes, true);
        else if (routesChanges.some(change => isOneOfRegexes(nonAnimatedChanges, change)))
            updateRoutes(newRoutes);
    }, [props.routes]);

    const onGroupClick = (routeId) => {
        if (props.onGroupClick)
            props.onGroupClick(routeId);
    };

    const onRemoveStopFromRouteClick = (routeId, stopId) => {
        if (props.onRemoveStopFromRouteClick)
            props.onRemoveStopFromRouteClick(routeId, stopId);
    };

    const onStopDoubleClick = (stopId) => {
        if (props.onStopDoubleClick)
            props.onStopDoubleClick(stopId);
    };

    const reorderRoute = async (routeId, reordered) => {
        const newRouteOrderIds = reordered.map(deliveryOrder => deliveryOrder.order_id);

        const lockGroupReq = {
            bid: appContext.bid,
            groupId: routeId,
            orderIds: newRouteOrderIds,
            isOrdered: true,
            isOrderChanged: true
        };

        const lockData = props.lockedRoutesIndex[routeId];

        if (lockData) {
            lockGroupReq.isRouteOrdersChanged = !!lockData.is_route_orders_changed;
            lockGroupReq.isMessengerChanged = !!lockData.is_messenger_changed;

            if (lockData.messenger_id)
                lockGroupReq.messengerId = lockData.messenger_id;
        }

        await props.lockDeliveryGroup(lockGroupReq);
    };

    function onDragEnd(result) {
        if (isDraggingOutside)
            setIsDraggingOutside(false);

        if (!result.destination) {
            setDrag(null);
            return;
        }

        if (result.source.droppableId === result.destination.droppableId &&
            result.source.index === result.destination.index) {
            setDrag(null);
            return;
        }

        const sourceRouteId = result.source.droppableId.replace("route-", "");
        const sourceRoute = routes.find(r => r.id === sourceRouteId);

        if (!sourceRoute) {
            setDrag(null);
            return;
        }

        if (result.source.droppableId === result.destination.droppableId) {
            const reOrderedStops = reorder(
                sourceRoute.route,
                result.source.index,
                result.destination.index
            );

            const newRoutes = [...routes];
            const routeIndex = newRoutes.findIndex(r => r.id === sourceRouteId);
            newRoutes[routeIndex] = {...sourceRoute, route: reOrderedStops};
            setRoutes(newRoutes);

            reorderRoute(sourceRouteId, reOrderedStops);
        } else {
            const destinationRouteId = result.destination.droppableId.replace("route-", "");
            const orderId = result.draggableId.replace("stop-", "");

            const newRoutes = [...routes];
            const sourceRouteIndex = newRoutes.findIndex(r => r.id === sourceRouteId);

            if (sourceRoute.route.length === 1)
                newRoutes.splice(sourceRouteIndex, 1);
            else {
                const newSourceRoute = {...sourceRoute, route: [...sourceRoute.route]};
                newRoutes[sourceRouteIndex] = newSourceRoute;

                const stopIndex = newSourceRoute.route.findIndex(r => r.order_id === orderId);
                newSourceRoute.route.splice(stopIndex, 1);
            }

            setRoutes(newRoutes);

            if (props.onMoveOrderToRoute)
                props.onMoveOrderToRoute(orderId, sourceRouteId, destinationRouteId);
        }

        setDrag(null);
    }

    function onDragStart(result) {
        setDrag({
            draggableId: result.draggableId,
            source: result.source,
            destination: result.destination
        });
    }

    function onDragUpdate(result) {
        setDrag({
            draggableId: result.draggableId,
            source: result.source,
            destination: result.destination
        });

        if ((!result.destination) ||
            (result.source.droppableId !== result.destination.droppableId)) {
            if (!isDraggingOutside) {
                const sourceRouteId = result.source.droppableId.replace("route-", "");
                const sourceRoute = routes.find(r => r.id === sourceRouteId);

                if (sourceRoute && sourceRoute.route.length > 1)
                    setIsDraggingOutside(true);
            }
        } else if (result.destination && isDraggingOutside)
            setIsDraggingOutside(false);
    }

    return (
        <div className="proposed-routes-content">
            <div className="proposed-routes-title-container proposed-routes-title-container-absolute">
                <div className="proposed-routes-title">
                    <div className="proposed-routes-title-text">
                        מסלולים ממתינים
                    </div>
                </div>
            </div>
            <div className="proposed-routes-title-container proposed-routes-title-container-hidden">
                <div className="proposed-routes-title">
                    <div className="proposed-routes-title-text">
                        מסלולים ממתינים
                    </div>
                </div>
            </div>
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart} onDragUpdate={onDragUpdate}>
                <div className="proposed-routes-list">
                    {
                        routes.map(route => {
                            return (
                                <React.Fragment
                                    key={`proposed-route-${route.id}`}>
                                    <ProposedRouteContainer onGroupClick={() => onGroupClick(route.id)}
                                                            onRemoveStopFromRouteClick={(stopId) => onRemoveStopFromRouteClick(route.id, stopId)}
                                                            onStopDoubleClick={onStopDoubleClick}
                                                            route={route}
                                                            lockData={props.lockedRoutesIndex[route.id]}
                                                            drag={drag}
                                    />
                                </React.Fragment>
                            );
                        })
                    }
                    <div
                        className={'drag-to-new-route' + (!isDraggingOutside ? ' drag-to-new-route-hidden' : '')}>
                        <DragToNewRoute isDraggingOutside={isDraggingOutside}/>
                    </div>
                </div>
            </DragDropContext>
        </div>
    );
}